import {useMutation} from '@apollo/client';
import i18next from 'i18next';

import {useRestMutation} from '@famly/stat_apollo-query';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import type {
    CreatePlanMutation,
    CreatePlanMutationVariables,
} from 'web-app/react/plans/manage-plan/__generated__/queries.api-types';
import {CreatePlan as CreatePlanGQL} from 'web-app/react/plans/manage-plan/queries';
import type {
    DeletePlanMutationResult,
    DeletePlanMutationVariables,
} from 'web-app/api/clients/rest-types/delete-plan/delete-plan';
import {DeletePlan} from 'web-app/react/simplified-us-billing-plan/queries/delete-plan.rest-mutation';
import {isNonEmpty} from '@famly/stat_ts-utils_non-empty-array';
import * as SentryService from 'web-app/services/sentry';

import {analyzePlanOverlaps} from '../helpers/analyze-plan-overlaps';
import type {DateRangeExclusive} from '../helpers/date-range';
import {type Child, type Plan} from '../helpers/plan-types';
import {transformPlanToPlanInput} from '../helpers/transform-plan-to-plan-input';
import {CreatePlan, type CreatePlanInput, type CreatePlanVariables} from '../queries/create-plan.rest-mutation';
import {useValidatePlans} from './use-validate-plan';
import {replacePlanWithBreakWord} from '../helpers/plan-breaker';

type CreatePlansFunctions = {
    /**
     * Creates a number of plans.
     * @param plans The plans to create.
     */
    createPlans: (plans: Plan[], children: Child[], breakRange: DateRangeExclusive) => Promise<void>;
};

/**
 * Method to create a number of plans.
 * @returns A record `{createPlans}` with a function to create plans.
 */
export const useCreatePlansMutation = (siteId: string): CreatePlansFunctions => {
    const [createPlanMutation] = useRestMutation<CreatePlanInput, CreatePlanVariables>(CreatePlan);
    const [editBillingPlan] = useMutation<CreatePlanMutation, CreatePlanMutationVariables>(CreatePlanGQL);
    const [deletePlan] = useRestMutation<DeletePlanMutationResult, DeletePlanMutationVariables>(DeletePlan);
    const {validatePlans: validatePlansGQL} = useValidatePlans();

    const createPlan = async (plan: Plan): Promise<void> => {
        const response = await createPlanMutation({variables: {input: {plan}}});
        if (!hasValue(response.data)) {
            return Promise.reject('Did not get a response from the plans mutation.');
        }
        return Promise.resolve();
    };

    const validatePlans = async (plans: Plan[], siteId: string): Promise<void> => {
        const validationResult = await validatePlansGQL(plans, siteId);

        if (!hasValue(validationResult.data) || !isNonEmpty(validationResult.data.finance.plans.preview)) {
            if (!hasValue(validationResult.data)) {
                SentryService.captureMessage('The PreviewPlans request did not return any data.', {
                    tags: {crew: 'GROWTH'},
                });
            } else {
                SentryService.captureMessage('The PreviewPlans request returned an empty preview.', {
                    extra: {
                        validateResultData: validationResult.data,
                    },
                    tags: {crew: 'GROWTH'},
                });
            }

            return Promise.reject([i18next.t('react.scheduleBreak.translations.json.planValidationFailed')]);
        }

        const errors = validationResult.data.finance.plans.preview.flatMap<string>(plan => {
            const warningBehavior = plan.behaviors.find(behavior => behavior.id === 'ShowPlanWarnings');
            if (!hasValue(warningBehavior)) {
                return [];
            }

            // This payload parsing assumes a specific response structure, particularly when accessing `warnings`.
            // This approach is potentially risky, as it may cause runtime errors if the structure changes or is not as expected.
            // Ideally, we should use a validation/parsing library like Zod to ensure type safety and guard against malformed responses.
            const {warnings} = JSON.parse(warningBehavior.payload ?? '{warnings: []}');
            return warnings.map(warning => warning.message);
        });

        const uniqueErrors = Array.from(new Set(errors));

        if (isNonEmpty(uniqueErrors)) {
            const uniqueErrorsWithReplacedWords = uniqueErrors.map(replacePlanWithBreakWord);
            return Promise.reject(uniqueErrorsWithReplacedWords);
        }

        return Promise.resolve();
    };

    const adjustOverlappingPlans = async (breakRange: DateRangeExclusive, children: Child[]): Promise<void> => {
        if (!breakRange.toExclusive) {
            return Promise.resolve();
        }

        const newStartDate = breakRange.toExclusive.clone().format('YYYY-MM-DD');

        for (const child of children) {
            const actions = analyzePlanOverlaps(child, breakRange, newStartDate);
            const plansToValidate = actions.filter(action => action.type === 'edit').map(action => action.plan);

            if (isNonEmpty(plansToValidate)) {
                await validatePlans(plansToValidate, siteId);
            }

            for (const action of actions) {
                if (action.type === 'delete' && hasValue(action.plan.planId)) {
                    await deletePlan({
                        variables: {
                            planId: action.plan.planId,
                        },
                    });
                } else if (action.type === 'edit') {
                    await editBillingPlan({
                        variables: {
                            planInputs: [transformPlanToPlanInput(action.plan)],
                            siteId,
                        },
                    });
                }
            }
        }

        return Promise.resolve();
    };

    const createPlansSequentially = async (
        plans: Array<Plan>,
        children: Array<Child>,
        breakRange: DateRangeExclusive,
    ): Promise<void> => {
        // Ensure that plans are created sequentially. We have observed that
        // weird stuff can happen if plans are created concurrently for the
        // same child.

        if (!isNonEmpty(plans)) {
            return Promise.reject(["A break can't be created because there's no existing plan for this child"]);
        }

        // Validate initial break creation
        await validatePlans(plans, siteId);

        // Adjust overlapping plans
        await adjustOverlappingPlans(breakRange, children);

        for (const plan of plans) {
            // Validate creating break with correct date range after adjusting overlapping plans
            await validatePlans(plans, siteId);
            await createPlan(plan);
        }
        return Promise.resolve();
    };

    return {createPlans: createPlansSequentially};
};
